export type ServerClientContext = {
  id: string;
  name: string;
  googleSignInEnabled: boolean;
  microsoftSignInEnabled: boolean;
  appleSignInEnabled: boolean;
};

export type ClientContext = {
  registerUrl: string;
  registerArrivalWithoutLoginUrl?: string;
  standaloneForgotPasswordBackUrl: string;
  manageBookingUrl?: string;
  errorContinueUrl: string;
  upgradeBackUrl?: string;
  displayBackOnLoginPage: boolean;
  ownersStartAuth: string;
  havenStartAuth: string;
  isAddressRequiredForRegistration?: boolean;
  isThirdParty?: boolean;
  consentDescription?: string;
} & ServerClientContext;

export type FederatedLoginProvider = 'google' | 'microsoft' | 'apple';

type SocialLoginConfig = Pick<
  ServerClientContext,
  'googleSignInEnabled' | 'microsoftSignInEnabled' | 'appleSignInEnabled'
>;

export const getClientContext = (
  clientId: string | undefined,
  socialLoginConfig: SocialLoginConfig = {
    googleSignInEnabled: false,
    microsoftSignInEnabled: false,
    appleSignInEnabled: false,
  },
): ClientContext => {
  const havendomain = process.env.HAVEN_DOMAIN || 'https://www.haven.com';
  const ownersdomain = process.env.OWNERS_DOMAIN || 'https://owners.haven.com';
  const ownersLogin = process.env.OWNERS_LOGIN_DOMAIN || 'https://ownerslogin.haven.com';
  const arrivalWithoutLogin =
    process.env.REGISTER_ARRIVAL_WITHOUT_LOGIN_URL || 'https://ownerarrivals.haven.com';
  const foodOrderingDomain = process.env.FOOD_ORDERING_DOMAIN || 'https://food.haven.com';
  const havenPaymentsDomain = process.env.HAVEN_PAYMENTS_DOMAIN || 'https://pay.haven.com';
  const havenGuestAppDomain =
    process.env.HAVEN_GUEST_APP_DOMAIN ||
    'com.bourne.haven://redirect?error=access_denied&error_description=Error';
  const ownersStartAuth = `${ownersLogin}?redirect=${ownersdomain}`;
  const havenStartAuth = `${havendomain}/my-account/login-registration?returnUrl=${havendomain}`;
  const { googleSignInEnabled, microsoftSignInEnabled, appleSignInEnabled } = socialLoginConfig;

  const commonProps = {
    id: clientId || '',
    registerUrl: '/register',
    googleSignInEnabled,
    microsoftSignInEnabled,
    appleSignInEnabled,
    ownersStartAuth,
    havenStartAuth,
  };

  switch (clientId) {
    case 'owners':
    case 'owners_jwt_io':
      return {
        ...commonProps,
        name: 'Owners',
        registerArrivalWithoutLoginUrl: arrivalWithoutLogin,
        standaloneForgotPasswordBackUrl: ownersLogin,
        errorContinueUrl: ownersdomain,
        displayBackOnLoginPage: true,
      };
    case 'checkout':
    case 'checkout_mock':
      return {
        ...commonProps,
        name: 'Checkout',
        standaloneForgotPasswordBackUrl: `${havendomain}/booking/addons`,
        errorContinueUrl: `${havendomain}/booking/addons`,
        upgradeBackUrl: `${havendomain}/booking/addons`,
        displayBackOnLoginPage: true,
      };
    case 'my_account':
      return {
        ...commonProps,
        name: 'My Account',
        manageBookingUrl: `${havendomain}/my-account/lookup-your-booking`,
        standaloneForgotPasswordBackUrl: `${havendomain}/my-account`,
        errorContinueUrl: `${havendomain}/my-account`,
        displayBackOnLoginPage: true,
      };
    case 'havenguestapp':
      return {
        ...commonProps,
        name: 'Guest App',
        standaloneForgotPasswordBackUrl: `${havendomain}`,
        errorContinueUrl: `${havenGuestAppDomain}`,
        displayBackOnLoginPage: false,
      };
    case 'experience_web':
      return {
        ...commonProps,
        name: 'Haven Experience',
        standaloneForgotPasswordBackUrl: `${havendomain}/experience`,
        errorContinueUrl: `${havendomain}/experience`,
        displayBackOnLoginPage: true,
      };
    case 'food_ordering':
      return {
        ...commonProps,
        id: clientId,
        name: 'Food Ordering',
        standaloneForgotPasswordBackUrl: `${foodOrderingDomain}`,
        errorContinueUrl: `${foodOrderingDomain}`,
        displayBackOnLoginPage: true,
      };
    case 'day_pass':
    case 'day_pass_jwt_io':
      return {
        ...commonProps,
        id: clientId,
        name: 'Day Pass',
        standaloneForgotPasswordBackUrl: `${havendomain}/experience`,
        errorContinueUrl: `${havendomain}/experience`,
        displayBackOnLoginPage: true,
        isAddressRequiredForRegistration: true,
      };
    case 'haven_payments':
      return {
        ...commonProps,
        id: clientId,
        name: 'Haven Payments',
        standaloneForgotPasswordBackUrl: `${havenPaymentsDomain}`,
        errorContinueUrl: `${havenPaymentsDomain}`,
        displayBackOnLoginPage: true,
      };
    case 'ownership_benefits':
      return {
        ...commonProps,
        id: clientId,
        name: 'Ownership Benefits',
        standaloneForgotPasswordBackUrl: `${ownersdomain}/ownership-cards`,
        errorContinueUrl: `${ownersdomain}/ownership-cards`,
        displayBackOnLoginPage: true,
      };
    case 'owners_reward_gateway':
      return {
        ...commonProps,
        id: clientId,
        name: 'My Haven Extras',
        standaloneForgotPasswordBackUrl: ownersdomain,
        errorContinueUrl: ownersdomain,
        displayBackOnLoginPage: true,
        isThirdParty: true,
        consentDescription:
          'The Owner Rewards programme would like access to some of your Haven account information to enable access via Rewards Gateway.',
      };
    case 'third_party_test':
      return {
        ...commonProps,
        id: clientId,
        name: 'Third Party Test App',
        standaloneForgotPasswordBackUrl: havendomain,
        errorContinueUrl: havendomain,
        displayBackOnLoginPage: true,
        isThirdParty: true,
      };
    default:
      return {
        ...commonProps,
        name: 'Haven',
        manageBookingUrl: `${havendomain}/my-account/lookup-your-booking`,
        standaloneForgotPasswordBackUrl: `${havendomain}/my-account`,
        errorContinueUrl: `${havendomain}/my-account`,
        displayBackOnLoginPage: true,
      };
  }
};

export const isOwnerClientId = (clientId: string = ''): boolean => !!clientId.match(/owners/);
